import React from "react"
import { SEOHead } from "components/SEO"
import { generateProductPageGraphSchema } from "utils/structuredData"
import FAQSection from "components/pages/api/FAQSection"
import GetStartedSection from "components/pages/api/GetStartedSection"
import HeroSection from "components/pages/api/HeroSection"
import LinesBackground from "components/pages/api/LinesBackground"
import UseCasesSection from "components/pages/api/UseCasesSection"
import Layout from "components/ui/Layout"
import DarkSection from "components/ui/Layout/DarkSection"

function API({ location: { pathname } }) {
  return (
    <Layout
      headerFloating
      headerDark
      trackingName="API"
      pageName="api"
      pagePath={pathname}
    >
      <DarkSection>
        <LinesBackground />
        <HeroSection />
        <UseCasesSection />
      </DarkSection>
      <FAQSection />
      <GetStartedSection />
    </Layout>
  )
}

const apiFaqs = [
  {
    question: "What can I do with the Preset API?",
    answer:
      "The Preset API lets you manage workspaces, user access, and BI assets programmatically. Teams use it to automate permission management, sync assets across environments, and treat Superset configuration as code.",
  },
  {
    question: "Is the Preset API based on Apache Superset's API?",
    answer:
      "Yes. The Preset API extends Apache Superset's native REST API with additional endpoints for Preset-specific resources like workspaces and organizations.",
  },
  {
    question: "How do I authenticate with the Preset API?",
    answer:
      "The Preset API uses JWT-based authentication. You generate an API key from your Preset account settings, then exchange it for a short-lived token to include in API requests.",
  },
  {
    question: "Can I manage users and workspaces via the API?",
    answer:
      "Yes. The API supports user lifecycle management, including inviting users, assigning roles, managing workspace membership, and configuring workspaces programmatically.",
  },
  {
    question: "Can I manage charts and dashboards as code?",
    answer:
      "Yes. The Preset API lets you import and export charts, dashboards, and datasets as YAML or JSON to support versioning, CI/CD workflows, and multi-environment deployments.",
  },
  {
    question: "Does the API support multi-workspace management?",
    answer:
      "Yes. The Preset API operates at the organization level so teams can manage multiple workspaces from a single API client.",
  },
]

// Export a Head component for Gatsby Head API
export function Head() {
  const graphSchema = generateProductPageGraphSchema({
    pageUrl: "https://preset.io/api/",
    pageTitle:
      "Apache Superset™ API: Manage Workspaces & Assets as Code | Preset",
    productName: "Preset API",
    applicationCategory: "DeveloperApplication",
    description:
      "The Preset API helps DevOps, engineering, and data teams manage Preset workspaces, users, permissions, and BI assets as code.",
    featureList: [
      "Workspace management",
      "User and team management",
      "Permissions management",
      "Database connection management",
      "Dataset management",
      "Chart management",
      "Dashboard management",
      "Annotation layer management",
      "Reports",
      "Import and export assets as YAML or JSON",
      "JWT-based authentication",
      "Multi-workspace management",
    ],
    faqs: apiFaqs,
    offers: {
      "@type": "Offer",
      url: "https://preset.io/api/",
      availability: "https://schema.org/InStock",
      category: "API Access",
      description: "Request access to the Preset API.",
    },
    breadcrumbs: [
      { name: "Home", url: "https://preset.io/" },
      { name: "API", url: "https://preset.io/api/" },
    ],
  })

  return (
    <SEOHead
      title="Apache Superset™ API: Manage Workspaces & Assets as Code"
      description="The Preset API brings Apache Superset under programmatic control. Manage user access, workspaces, and analytics assets as code from your own tooling."
      structuredData={[graphSchema]}
    />
  )
}

export default API
